import { useEffect, useRef, useState } from "react";
import { z } from "zod";

const zPersistStrategy = z.enum(["ON_CHANGE", "ON_UNMOUNT"]);
const PersistStrategy = zPersistStrategy.Values;
type PersistStrategy = z.infer<typeof zPersistStrategy>;

type Options = {
  storage?: Storage | undefined;
  persistStrategy?: PersistStrategy | undefined;
};

function usePersistedState<T>(key: string, defaultValue: T | (() => T), options?: Options) {
  const { storage = localStorage, persistStrategy = PersistStrategy.ON_CHANGE } = options ?? {};

  const [value, setValue] = useState(() => {
    const receivedDefaultValue =
      typeof defaultValue === "function" ? (defaultValue as () => T)() : defaultValue;
    const persistedValue = storage.getItem(key);
    if (persistedValue === null) return receivedDefaultValue;

    try {
      const parsedValue = JSON.parse(persistedValue) as T;
      return parsedValue;
    } catch {
      return receivedDefaultValue;
    }
  });

  const latestRef = useRef({ key, value, storage, persistStrategy });
  latestRef.current = { key, value, storage, persistStrategy };

  useEffect(() => {
    if (persistStrategy === PersistStrategy.ON_CHANGE) {
      setItem(storage, key, value);
    }
  }, [key, persistStrategy, storage, value]);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const persistValueIfStrategyIsUnmount = () => {
      const { key, value, storage, persistStrategy } = latestRef.current;
      if (persistStrategy === PersistStrategy.ON_UNMOUNT) {
        setItem(storage, key, value);
      }
    };

    window.addEventListener(
      "beforeunload",
      () => {
        persistValueIfStrategyIsUnmount();
      },
      {
        signal,
      },
    );

    return () => {
      controller.abort();
      persistValueIfStrategyIsUnmount();
    };
  }, []);

  return [value, setValue] as const;
}

function setItem<T>(storage: Storage, key: string, value: T) {
  storage.setItem(key, JSON.stringify(value));
}

export { PersistStrategy, usePersistedState };
